import React from 'react';
import { ShopScreen } from './ShopScreen';
import { Link, useHistory } from 'react-router-dom';

import { ReactComponent as PlaneIcon } from '../../media/images/plane.svg';
import { ReactComponent as ShipIcon } from '../../media/images/ship.svg';
import { ReactComponent as HotelIcon } from '../../media/images/hotel.svg';
import { Btn } from 'components/btn/Btn';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import { faUserPlus, faPhoneLaptop, faTags } from '@fortawesome/pro-light-svg-icons';
import { ReactComponent as ExternalLink } from '../../media/images/external-link.svg';
import { ReactComponent as Location } from '../../media/images/location.svg';
import ResponsiveContainer, {
    ContainerType,
} from 'components/responsive-containers/ResponsiveContainer';
import { Routes } from 'routes';

const Header = () => {
    return (
        <div>
            <div className="flex justify-center items-center bg-pale-blue text-[28px] md:text-[42px] p-6 md:p-12 text-brand-dark md:mb-2 flex-col">
                <p className="font-[600]">Earn More</p>
                <p className="text-[16px] md:text-[22px] pb-2 mt-[30px] text-brand-dark text-center">
                    Earn when you book travel, dine at local restaurants, and more!
                </p>
            </div>
        </div>
    );
};

type CardProps = {
    isNew?: boolean;
    className?: string;
    navigateTo?: string;
};

const Card = ({
    children,
    isNew,
    className = '',
    navigateTo,
}: React.PropsWithChildren<CardProps>) => {
    const defaultStyles = 'flex items-center justify-center border border-grey-light rounded-md';
    const isNewStyles =
        'relative border border-orange rounded-md py-4 px-4 md:px-4 md:py-[16px] xl:px-12 xl:py-[26px] shadow-lg';
    const content = (
        <>
            {isNew && (
                <span className="text-sm absolute top-[-12px] md:left-11 left-4 bg-orange rounded-full text-white px-3 py-1 font-semibold">
                    New!
                </span>
            )}
            {children}
        </>
    );

    const classes = isNew ? `${isNewStyles} ${className}` : `${defaultStyles} ${className}`;

    if (navigateTo) {
        return (
            <Link to={navigateTo} className={classes} style={{ textDecoration: 'none' }}>
                {content}
            </Link>
        );
    }

    return <div className={classes}>{content}</div>;
};

const MoreWaysRow = ({
    icon,
    label,
    to,
    recommended,
}: {
    icon: React.ReactNode;
    label: React.ReactNode;
    to: string;
    recommended?: boolean;
}) => (
    <Link
        to={to}
        className="flex items-center justify-between md:py-4 py-2 md:h-[100px] h-[72px] md:pr-5 pr-2 border-b border-grey-light hover:bg-pale-blue transition"
        style={{ textDecoration: 'none' }}
    >
        <div className="flex items-center gap-4">
            {icon}
            <span
                className={`md:text-[24px] text-[16px] text-brand-dark font-[600] ${
                    recommended ? 'flex flex-col' : ''
                }`}
            >
                {recommended && (
                    <span className="md:text-[14px] text-[12px] text-bonus-pink font-semibold align-middle">
                        RECOMMENDED
                    </span>
                )}
                {label}
            </span>
        </div>
        <FontAwesomeIcon
            icon={faChevronRight}
            className="text-brand md:w-[24px] md:h-[24px] w-[16px] h-[16px]"
        />
    </Link>
);

export const EarnMorePage = () => {
    const history = useHistory();
    return (
        <ShopScreen>
            <Header />
            <div className="lg:px-[140px] px-[24px] lg:py-[60px] py-[28px] items-center flex flex-col">
                <div className="flex justify-between md:max-w-[1160px] max-w-full w-full items-center">
                    <p className="text-dark-blue md:text-[29px] text-[20px] font-semibold">
                        Earn When You Travel
                    </p>
                    <Link
                        className="no-underline text-[14px] md:text-[18px] font-semibold text-brand"
                        to={''}
                    >
                        See All <FontAwesomeIcon size="sm" icon={faChevronRight} className="ml-1" />
                    </Link>
                </div>
                <div className="flex gap-5 md:mt-7 mt-3 md:max-w-[1160px] w-full md:px-0 justify-between md:justify-start sm:flex-wrap md:flex-wrap lg:flex-nowrap flex-nowrap">
                    {/* replace the navigate two to the flights category route */}
                    <Card
                        className="w-full md:w-auto"
                        navigateTo={`${Routes.ShopEarnMoreByType}`.replace(':typeId', 'flights')}
                    >
                        <div className="md:min-w-[270px] items-center flex flex-row md:flex-col py-2 px-1 md:px-6 md:py-4 md:text-center gap-1 md:gap-0">
                            <PlaneIcon className="mx-auto md:w-[100px] w-[48px] md:mt-[-4px] text-brand" />
                            <div>
                                <p className="md:text-[24px] text-[14px] text-dark-blue font-semibold md:mt-2">
                                    Flight
                                </p>
                                <p className="md:text-[24px] text-[14px] text-dark-blue font-semibold">
                                    Gift Cards
                                </p>
                            </div>
                        </div>
                    </Card>
                    <Card
                        className="w-full md:w-auto"
                        navigateTo={`${Routes.ShopEarnMoreByType}`.replace(':typeId', 'cruises')}
                    >
                        <div className="md:min-w-[270px] items-center flex flex-row md:flex-col py-2 px-1 md:px-6 md:py-4 md:text-center gap-2 md:gap-0">
                            <ShipIcon className="mx-auto md:w-[48px] w-[36px] text-brand" />
                            <div>
                                <p className="md:text-[24px] text-[14px] text-dark-blue font-semibold md:mt-2">
                                    Cruise
                                </p>
                                <p className="md:text-[24px] text-[14px] text-dark-blue font-semibold">
                                    Gift Cards
                                </p>
                            </div>
                        </div>
                    </Card>
                    <ResponsiveContainer width={710} For={ContainerType.desktop}>
                        <Card isNew>
                            <div className="flex gap-10 w-full">
                                <HotelIcon className="self-center lg:w-[140px] lg:h-auto w-[60px] h-[48px] text-brand " />
                                <div>
                                    <p className="md:text-[24px] text-[14px] text-dark-blue font-semibold">
                                        Book a Hotel or Car
                                    </p>
                                    <p className="text-xl mt-2 text-grey-darkest">
                                        <span className="font-semibold">Earn up to 20%</span> when
                                        you book travel with a credit or debit card.
                                    </p>
                                    <div className="flex gap-8 mt-4 items-center">
                                        <Btn>
                                            Book Today{' '}
                                            <ExternalLink className="inline-block text-white ml-1 -mt-1 w-3 h-3" />
                                        </Btn>
                                        <Link
                                            className="text-xl font-semibold underline-offset-8"
                                            to={`${Routes.HowItWorksBookTravel}?utm_source=raiseright_web&utm_medium=earn_more&utm_campaign=book_travel`}
                                        >
                                            Learn More
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </Card>
                    </ResponsiveContainer>
                </div>

                <ResponsiveContainer width={710} For={ContainerType.mobile}>
                    <div className="flex gap-5 mt-5 w-full">
                        <Card isNew>
                            <div className="flex gap-5 w-full">
                                <HotelIcon className="mt-3 min-w-[18%] w-[58px] h-[46px] text-brand" />
                                <div className="pl-[2px]">
                                    <p className="text-[16px] text-brand-dark font-semibold">
                                        Book a Hotel or Car
                                    </p>
                                    <p className="text-[14px] text-grey-darkest">
                                        <span className="font-semibold">Earn up to 20%</span> when
                                        you book travel with a credit or debit card.
                                    </p>
                                    <div className="flex gap-4 mt-4 justify-between items-center">
                                        <Btn isSmall className="py-2 w-2/3 text-[14px]">
                                            Book{' '}
                                            <ExternalLink className="inline-block text-white ml-1 -mt-1 w-2 h-2" />
                                        </Btn>
                                        <Link
                                            className="text-[14px] w-full font-semibold underline-offset-6 pb-1"
                                            to={`${Routes.HowItWorksBookTravel}?utm_source=raiseright_web&utm_medium=earn_more&utm_campaign=book_travel`}
                                        >
                                            Learn More
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </Card>
                    </div>
                </ResponsiveContainer>

                <div className="md:mt-18 mt-10 mb-2 max-w-[1160px] w-full">
                    <p className="text-dark-blue md:text-[29px] text-[20px] font-semibold">
                        Earn at Restaurants Nearby
                    </p>
                </div>
                <Card className="flex md:items-center items-start px-4 md:px-8 py-6 md:mt-6 mt-1 max-w-[1160px] w-full">
                    <div className="flex md:self-center self-start justify-center pt-0 lg:min-w-[8%] min-w-[18%] mr-[16px] ">
                        <Location className="md:w-[60px] md:h-[78px] w-auto h-[50px] text-brand" />
                    </div>
                    <div className="flex md:flex-row flex-col items-start md:items-center gap-2">
                        <div className="flex-1 md:mr-12 mr-2">
                            <p className="md:text-[24px] text-[16px] text-dark-blue font-semibold mb-1">
                                Local Dining
                            </p>
                            <p className="md:text-[18px] text-[14px] font-normal text-grey-darkest md:pr-1">
                                <strong>Earn 5%</strong> every time you dine at participating local
                                restaurants and pay with a linked credit or debit card. Find
                                participating restaurants and link a card in the{' '}
                                <a
                                    href="https://raiseright.onelink.me/Gei8/diningsearch4"
                                    className="text-brand underline underline-offset-4"
                                >
                                    RaiseRight app
                                </a>
                                .
                            </p>
                        </div>
                        <ResponsiveContainer width={710} For={ContainerType.desktop}>
                            <Btn
                                onClick={() => {
                                    history.push(
                                        `${Routes.HowItWorksLocalDining}?utm_source=raiseright_web&utm_medium=earn_more&utm_campaign=local_dining`
                                    );
                                }}
                            >
                                Learn More{' '}
                                <ExternalLink className="inline-block text-white ml-1 -mt-1 w-3 h-3" />
                            </Btn>
                        </ResponsiveContainer>
                        <ResponsiveContainer width={710} For={ContainerType.mobile}>
                            <Btn
                                isSmall
                                className="py-2 w-[140px] h-[40px] text-[14px] md:mt-0 mt-2"
                                onClick={() => {
                                    history.push(
                                        `${Routes.HowItWorksLocalDining}?utm_source=raiseright_web&utm_medium=earn_more&utm_campaign=local_dining`
                                    );
                                }}
                            >
                                Learn More{' '}
                                <ExternalLink className="inline-block text-white ml-1 -mt-1 md:w-3 md:h-3 w-2 h-2" />
                            </Btn>
                        </ResponsiveContainer>
                    </div>
                </Card>

                <div className="flex md:flex-row flex-col items-center md:mt-12 mt-10 mb-2 max-w-[1160px] w-full justify-center ">
                    <div className="md:mr-6 md:w-[30%] w-full mb-0">
                        <p className="text-dark-blue md:text-[28px] text-[20px] font-semibold">
                            More Ways to Earn
                        </p>
                    </div>
                    <div className="bg-white md:w-[70%] w-full">
                        <MoreWaysRow
                            icon={
                                <FontAwesomeIcon
                                    className="md:w-14 md:h-14 w-8 h-8 md:ml-5 ml-2 md:mr-6 mr-2 text-brand"
                                    icon={faUserPlus}
                                />
                            }
                            label="Invite Friends & Family"
                            to={`${Routes.ShopAccountSettingsInviteFriendsFamilyOptionsTab}?utm_source=raiseright_web&utm_medium=earn_more&utm_campaign=invite_frends_and_family`}
                            recommended
                        />
                        <MoreWaysRow
                            icon={
                                <FontAwesomeIcon
                                    icon={faPhoneLaptop}
                                    className="md:w-14 md:h-14 w-8 h-8 md:ml-5 ml-2 md:mr-6 mr-2 text-brand"
                                />
                            }
                            label="Add the Browser Extension"
                            to={`${Routes.HowItWorksBrowserExtension}?utm_source=raiseright_web&utm_medium=earn_more&utm_campaign=browser_extension`}
                        />
                        <MoreWaysRow
                            icon={
                                <FontAwesomeIcon
                                    icon={faTags}
                                    className="md:w-14 md:h-14 w-8 h-8 md:ml-5 ml-2 md:mr-6 mr-2 text-brand"
                                />
                            }
                            label="Shop Top Online Deals & Coupons"
                            to={`${Routes.ShopTopDeals}?utm_source=raiseright_web&utm_medium=earn_more&utm_campaign=online_deals`}
                        />
                    </div>
                </div>
            </div>
        </ShopScreen>
    );
};
